import React, { useState } from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Button, Tooltip } from 'antd';
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';

import { ModalDelete } from '../components/ModalDelete';
import { useDownloadPDF } from '../hooks/useDownloadPDF';
import { TOAST_DURATION_RESOLUTION_886 } from '@/utils/constants';
import { useGetResolution886Report } from '@/hooks/v2/resolution-886';
import { ToastError } from '../../Resolution886/components/ToastError';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useDestroyResolution886Review } from '@/hooks/v2/resolution-886/useDestroyResolution886Review';
import type { ReviewVersionItem } from '@/types/Resolution886';
import type { SelectedReport } from '../types';
import * as S from './styles';

interface VersionTableProps {
	versions?: ReviewVersionItem[];
	reportId?: string;
}

export function VersionTable({ versions = [], reportId }: Readonly<VersionTableProps>): JSX.Element {
	const [versionToDelete, setVersionToDelete] = useState<SelectedReport | undefined>();
	const [downloadingPDFId, setDownloadingPDFId] = useState<string | undefined>();
	const [selectedReviewId, setSelectedReviewId] = useState<string | undefined>();
	const [toastMessage, setToastMessage] = useState<string>('');
	const [isToastErrorOpen, setIsToastErrorOpen] = useState<boolean>(false);

	const { organization, company } = useApplicationContext();
	const { isLoading: isDeleting, mutateAsync: deleteReview } = useDestroyResolution886Review();

	const { data: reportData } = useGetResolution886Report({
		organization_id: organization?.id!,
		company_id: company?.id!,
		id: reportId || ''
	});

	function isReviewConsolidated(versionId: string): boolean {
		return Number(reportData?.reviews.find((review) => review.id === versionId)?.consolidated) === 1;
	}

	const { onDownloadPDF, downloadPDFLoading } = useDownloadPDF({
		report: reportData || { id: '' },
		organizationId: organization?.id!,
		companyId: company?.id!,
		lastReviewId: selectedReviewId || ''
	});

	function handleOpenToastError(message: string): void {
		setToastMessage(message);
		setIsToastErrorOpen(true);

		setTimeout(() => {
			setIsToastErrorOpen(false);
		}, TOAST_DURATION_RESOLUTION_886);
	}

	async function downloadReportPDF(versionId: string): Promise<void> {
		setDownloadingPDFId(versionId);
		setSelectedReviewId(versionId);

		if (!isReviewConsolidated(versionId)) {
			handleOpenToastError(I18n.get('You must consolidate the last review before downloading the PDF'));
			return;
		}
		try {
			await onDownloadPDF({
				pdf_custom: [],
				reviewId: versionId
			});
		} catch (error) {
			console.error('Error downloading PDF:', error);
		} finally {
			setDownloadingPDFId(undefined);
		}
	}

	function openDeleteConfirmation(version: SelectedReport): void {
		setVersionToDelete(version);
	}

	function closeDeleteConfirmation(): void {
		setVersionToDelete(undefined);
	}

	async function handleOnDeleteReport(): Promise<void> {
		if (!versionToDelete || !organization?.id || !company?.id) return;
		try {
			await deleteReview({
				review_id: versionToDelete.id,
				report_id: versionToDelete.report_id || reportId || '',
				company_id: company.id,
				organization_id: organization.id
			});
			closeDeleteConfirmation();
		} catch (error) {
			console.error('Error deleting review:', error);
		}
	}

	function formatDate(dateString: string): string {
		return moment(dateString).format('DD/MM/YYYY');
	}

	return (
		<>
			{isToastErrorOpen && <ToastError message={toastMessage} />}
			{versions.map((version) => (
				<S.ListItem key={version.id}>
					<S.Block>
						<Row justify="space-between" align="middle">
							<Col xs={4}>
								<Row justify="center">
									<S.CustomTableText style={{ fontWeight: 600 }}>
										{I18n.get(version.name)}
									</S.CustomTableText>
								</Row>
							</Col>
							<S.VerticalDivider type="vertical" />
							<Col xs={14}>
								<Row justify="center">
									<S.CustomTableText>{I18n.get(version.file?.original_name)}</S.CustomTableText>
								</Row>
							</Col>
							<S.VerticalDivider type="vertical" />
							<Col xs={2}>
								<Row justify="center">
									<S.CustomTableText>{formatDate(version.creation_date)}</S.CustomTableText>
								</Row>
							</Col>
							<S.VerticalDivider type="vertical" />
							<Col xs={2}>
								<Row justify="end" align="middle" gutter={8}>
									<Col>
										<Button
											type="link"
											size="small"
											loading={downloadPDFLoading && downloadingPDFId === version.id}
											disabled={downloadingPDFId === version.id}
											style={{ display: 'flex', alignItems: 'center', height: 'auto' }}
											onClick={() => downloadReportPDF(version.id)}
											icon={
												<Tooltip title={I18n.get('Download PDF Report')}>
													<DownloadOutlined style={{ color: '#2F54EB', fontSize: '16px' }} />
												</Tooltip>
											}
										/>
									</Col>
									<Col>
										<Button
											type="link"
											size="small"
											loading={isDeleting}
											disabled={isDeleting}
											style={{ display: 'flex', alignItems: 'center', height: 'auto' }}
											onClick={() =>
												openDeleteConfirmation({
													id: version.id,
													report_name: version.name,
													report_id: version.report_id || reportId
												})
											}
											icon={
												<Tooltip title={I18n.get('Delete')}>
													<DeleteOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
												</Tooltip>
											}
										/>
									</Col>
								</Row>
							</Col>
						</Row>
					</S.Block>
				</S.ListItem>
			))}
			<ModalDelete
				open={!!versionToDelete}
				reportName={versionToDelete?.report_name}
				onConfirm={handleOnDeleteReport}
				onCancel={closeDeleteConfirmation}
			/>
		</>
	);
}
